import { StyleSheet, View, TouchableOpacity } from 'react-native'

import { translate } from '../../../i18n'
import { color, fontFamily } from '../../../theme'
import { DS } from '../../../design-system'
import { getLeaveGameStatsLines, type PlayerStats } from '../hooks/useLeaveGameStatsMessage'

interface LeaveGameModalProps {
  visible: boolean
  onCancel: () => void
  onLeave: () => void // Leave game (game continues for others)
  onFinish?: () => void // End game for all (host only)
  isHost?: boolean
  isPractice?: boolean
  playerStats?: PlayerStats
}

export const LeaveGameModal: React.FC<LeaveGameModalProps> = ({
  visible,
  onCancel,
  onLeave,
  onFinish,
  isHost = false,
  isPractice = false,
  playerStats
}) => {
  const statsLines = getLeaveGameStatsLines({
    numberOfHands: playerStats?.numberOfHands,
    numberOfHandsWon: playerStats?.numberOfHandsWon,
    totalChipsWon: playerStats?.totalChipsWon,
    totalChipsLost: playerStats?.totalChipsLost,
    rank: playerStats?.userStats?.rank ?? null
  })
  const leaveGameStatsMessage = statsLines.join('\n')

  if (!visible) {
    return null
  }

  return (
    <View style={styles.overlay}>
      {/* Backdrop - tap to close */}
      <TouchableOpacity style={styles.backdrop} activeOpacity={1} onPress={onCancel} />
      {/* Content */}
      <View style={styles.contentWrapper} pointerEvents="box-none">
        <View style={styles.modalContainer}>
          <View style={styles.container}>
            <DS.Text variant="Heading 2/Medium" textAlign="center">
              {translate('gameScreen.leaveGameModal.title')}
            </DS.Text>
            <DS.Text variant="Body Small/Regular" textAlign="center">
              {translate('gameScreen.leaveGameModal.message')}
            </DS.Text>
            {leaveGameStatsMessage && (
              <DS.Text variant="Body Small/Regular" textAlign="center">
                {leaveGameStatsMessage}
              </DS.Text>
            )}
            <DS.Frame gap="8" justifyContent="center" alignItems="center">
              {/* Leave Game - available to all players; practice shows "Save for Later" */}
              <DS.Button onPress={onLeave} variant="Medium/Primary" type="On Light" style={styles.button}>
                {isPractice
                  ? translate('gameScreen.leaveGameModal.saveForLater')
                  : translate('gameScreen.leaveGameModal.leaveOnly')}
              </DS.Button>

              {/* End Game - host only */}
              {isHost && onFinish && (
                <DS.Button onPress={onFinish} variant="Medium/Primary" type="On Dark" style={styles.button}>
                  {translate('gameScreen.leaveGameModal.endGame')}
                </DS.Button>
              )}
            </DS.Frame>
          </View>
        </View>
      </View>
    </View>
  )
}

const styles = StyleSheet.create({
  overlay: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 9999
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.2)'
  },
  contentWrapper: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: 'center',
    alignItems: 'center'
  },
  modalContainer: {
    backgroundColor: color.palette.cream,
    alignItems: 'center',
    marginHorizontal: 24,
    paddingHorizontal: 24,
    borderRadius: 20,
    width: '80%',
    maxWidth: 400
  },
  container: {
    width: '100%',
    paddingVertical: 24,
    gap: 20
  },
  button: {
    width: '100%'
  },
  buttonTitleStyle: {
    fontFamily: fontFamily.GTAmericaStandardMedium,
    fontSize: 16,
    paddingVertical: 5
  }
})
